/**
 * A crew's papers: uploading one, reading it, and listing what is on file.
 *
 * The order matters and it is the same order the business-card reader uses:
 * **the file is stored first, and read second.** A certificate that reached
 * the bucket is a certificate the office has, whatever the reader then makes
 * of it — and the reader is a network call to somebody else's service that can
 * be busy, down, or not configured yet. Uploading second would mean a bad day
 * at OpenAI loses the document.
 *
 * Nothing here decides anything. Which columns an answer becomes is
 * `crewExtractionPatch` in packages/core (rule 5), applied by the review
 * screen after a person has read what it says.
 */

import type { CrewDocument } from "@roof-hawk/core";

import { supabase, unwrap } from "./supabase";

const BUCKET = "crew-docs";

/** Everything on file for one crew, newest first. */
export async function fetchCrewDocuments(
  crewId: string,
): Promise<CrewDocument[]> {
  return unwrap<CrewDocument[]>(
    await supabase
      .from("crew_documents")
      .select("*")
      .eq("crew_id", crewId)
      .order("created_at", { ascending: false }),
  );
}

/**
 * Put the file in the bucket.
 *
 * Prefixed with the crew id so one crew's papers sit together, and stamped so
 * this year's certificate does not overwrite last year's — the old one is the
 * one a claim from last year is made against.
 */
export async function uploadCrewDocument(
  crewId: string,
  file: File,
): Promise<string> {
  const path = `${crewId}/${Date.now()}-${file.name.replace(/[^\w.-]+/g, "_")}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: file.type || "application/octet-stream",
  });
  if (error) throw new Error(error.message);
  return path;
}

/** A link good for an hour, for looking at what was uploaded. */
export async function signCrewDocument(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, 60 * 60);
  if (error) throw new Error(error.message);
  if (!data?.signedUrl) throw new Error("That document could not be opened.");
  return data.signedUrl;
}

/** Record the row. The extraction is kept as it came back, corrected or not. */
export async function recordCrewDocument(input: {
  crewId: string;
  kind: string;
  filename: string;
  storagePath: string;
  extraction: Record<string, unknown> | null;
  expiresOn: string | null;
  uploadedBy: string | null;
}): Promise<void> {
  const { error } = await supabase.from("crew_documents").insert({
    crew_id: input.crewId,
    kind: input.kind,
    filename: input.filename,
    storage_path: input.storagePath,
    extraction: input.extraction,
    expires_on: input.expiresOn,
    uploaded_by: input.uploadedBy,
  });
  if (error) throw new Error(error.message);
}

/**
 * Throw one away: the row, then the file.
 *
 * The row goes first. If the bucket then refuses, what is left is an orphaned
 * file nobody can see rather than a row pointing at a file that is gone — and
 * the second is the one that breaks the screen. The error is still reported,
 * so a file that would not delete is somebody's problem rather than nobody's.
 *
 * There is no undo. A crew's papers are the copy the office holds, not the
 * record of anything: the certificate itself is with the crew and their
 * insurer, and the point of deleting a W-9 is that the number on it stops
 * existing here.
 */
export async function deleteCrewDocument(document: {
  id: string;
  storage_path: string;
}): Promise<void> {
  const { error: rowError } = await supabase
    .from("crew_documents")
    .delete()
    .eq("id", document.id);
  if (rowError) throw new Error(rowError.message);

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .remove([document.storage_path]);
  if (error) throw new Error(error.message);
  // A refusal is not an error to Storage. When the bucket's policy will not
  // let this person delete, `remove` answers `{ data: [], error: null }` —
  // the same shape as success with nothing to do — and the W-9 stays in the
  // bucket while the screen says it is gone. Nothing deleted is a failure.
  if (!data?.length) {
    throw new Error(
      "The document was removed from the list, but its file could not be " +
        "deleted from storage. Ask an admin to remove it.",
    );
  }
}

/** The file as base64, which is what the reader takes. */
export async function fileAsBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  // In chunks: `String.fromCharCode(...bytes)` on a multi-megabyte PDF blows
  // the argument limit and throws a RangeError nobody could read.
  let binary = "";
  const CHUNK = 0x8000;
  for (let at = 0; at < bytes.length; at += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK));
  }
  return btoa(binary);
}

/**
 * Ask the reader what the document says.
 *
 * Returns the raw extraction, exactly as the model produced it. Turning it
 * into a patch for the crew row is core's job, and a person's before that.
 */
export async function readCrewDocument(
  file: File,
): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke(
    "read-crew-document",
    {
      body: {
        file: {
          media_type: file.type || "application/pdf",
          data: await fileAsBase64(file),
          filename: file.name,
        },
      },
    },
  );

  if (error) {
    // The function reports its own trouble in the body — "the reader is busy",
    // "not configured" — and that sentence is far more use than "Edge Function
    // returned a non-2xx status code".
    const detail = await readFunctionError(error);
    throw new Error(detail ?? error.message);
  }

  const payload = data as { extraction?: Record<string, unknown> } | null;
  if (!payload?.extraction) {
    throw new Error("The document reader gave an answer we could not read.");
  }
  return payload.extraction;
}

/** The message an Edge Function put in its response body, if it managed one. */
async function readFunctionError(error: unknown): Promise<string | null> {
  const context = (error as { context?: unknown }).context;
  if (!(context instanceof Response)) return null;
  try {
    const body = (await context.json()) as { error?: unknown };
    return typeof body.error === "string" ? body.error : null;
  } catch {
    return null;
  }
}
